import { useCallback, useRef } from 'react';
import { MortgageData, LoanTypeOption, PropertyType } from '@/types/calculator';
import { useMarketData } from '@/contexts/MarketDataContext';
import { useCalculatorLogger } from './useCalculatorLogger';

interface UseCalculatorHandlersProps {
  mortgageData: MortgageData;
  updateData: (updates: Partial<MortgageData>) => void;
}

export const useCalculatorHandlers = ({ mortgageData, updateData }: UseCalculatorHandlersProps) => {
  const { getInterestRateForLoanType, marketData } = useMarketData();
  const logger = useCalculatorLogger('CalculatorHandlers');
  
  // Circuit breaker refs for loan type changes
  const lastLoanTypeRef = useRef<LoanTypeOption>(mortgageData.loanType);
  const changeCountRef = useRef(0);
  const lastChangeTimeRef = useRef(Date.now());
  
  const handleHomePriceChange = useCallback((value: number) => {
    const newDownPayment = (mortgageData.downPaymentPercentage / 100) * value;
    updateData({ 
      homePrice: value, 
      downPayment: newDownPayment
    });
  }, [mortgageData.downPaymentPercentage, updateData]);
  
  const handleDownPaymentChange = useCallback((value: number) => {
    const newPercentage = (value / mortgageData.homePrice) * 100;
    updateData({ 
      downPayment: value, 
      downPaymentPercentage: newPercentage 
    });
  }, [mortgageData.homePrice, updateData]);
  
  const handleInterestRateChange = useCallback((value: number) => {
    updateData({ interestRate: value });
  }, [updateData]);
  
  const handleLoanTermChange = useCallback((value: number) => {
    updateData({ loanTerm: value });
  }, [updateData]);
  
  const handleLoanTypeChange = useCallback((value: LoanTypeOption) => {
    // Circuit breaker: skip if same value
    if (value === lastLoanTypeRef.current) {
      logger.logCircuitBreaker('loanType', `Duplicate value: ${value}`);
      return;
    }
    
    // Detect rapid changes (potential state loop)
    const now = Date.now();
    if (now - lastChangeTimeRef.current < 1000) {
      changeCountRef.current++;
      if (changeCountRef.current > 3) {
        logger.logCircuitBreaker('loanType', `Rapid changes detected (${changeCountRef.current} in 1s)`);
        return;
      }
    } else {
      changeCountRef.current = 1;
    }
    lastChangeTimeRef.current = now;
    
    // Log the change
    logger.logChange('loanType', lastLoanTypeRef.current, value, 'handleLoanTypeChange');
    lastLoanTypeRef.current = value;
    
    const updates: Partial<MortgageData> = { loanType: value };
    
    // Set default down payment percentages based on loan type
    let defaultPercentage: number;
    
    switch (value) {
      case 'conventional':
        defaultPercentage = 20;
        break;
      case 'fha':
        defaultPercentage = 3.5;
        break;
      case 'va':
      case 'usda':
        defaultPercentage = 0;
        break;
      default:
        defaultPercentage = 20;
    }
    
    const newDownPaymentAmount = (defaultPercentage / 100) * mortgageData.homePrice;
    
    updates.downPaymentPercentage = defaultPercentage;
    updates.downPayment = newDownPaymentAmount;
    
    // Update interest rate based on loan type if market data is available
    if (marketData) {
      updates.interestRate = getInterestRateForLoanType(value);
    }
    
    updateData(updates);
  }, [mortgageData.homePrice, updateData, marketData, getInterestRateForLoanType]);
  
  const handleZipCodeChange = useCallback((value: string) => {
    updateData({ zipCode: value });
  }, [updateData]);
  
  const handleIsVeteranChange = useCallback((value: boolean) => {
    updateData({ isVeteran: value });
  }, [updateData]);
  
  const handleStateChange = useCallback((value: string) => {
    updateData({ 
      state: value,
      location: { ...mortgageData.location, state: value }
    });
  }, [mortgageData.location, updateData]);
  
  const handleCityChange = useCallback((value: string) => {
    updateData({ 
      city: value,
      location: { ...mortgageData.location, city: value }
    });
  }, [mortgageData.location, updateData]);
  
  const handlePropertyTaxChange = useCallback((value: number) => {
    updateData({ propertyTax: value });
  }, [updateData]);
  
  const handleInsuranceChange = useCallback((value: number) => {
    updateData({ homeInsurance: value });
  }, [updateData]);
  
  const handleHOAChange = useCallback((value: number) => {
    updateData({ hoaFees: value });
  }, [updateData]);
  
  const handleCreditScoreChange = useCallback((value: number) => {
    updateData({ creditScore: value });
  }, [updateData]);
  
  const handlePropertyTypeChange = useCallback((value: PropertyType) => {
    updateData({ propertyType: value });
  }, [updateData]);
  
  const handleVADisabilityStatusChange = useCallback((value: string | null) => {
    updateData({ vaDisabilityStatus: value });
  }, [updateData]);
  
  const handleVAStatusChange = useCallback((value: string | null) => {
    updateData({ vaStatus: value });
  }, [updateData]);
  
  const handleHomebuyingHistoryChange = useCallback((value: string | null) => {
    updateData({ homebuyingHistory: value });
  }, [updateData]);
  
  const handleSellerCreditChange = useCallback((value: number) => {
    updateData({ sellerCredit: value });
  }, [updateData]);
  
  const handlePIDRateChange = useCallback((value: number) => {
    updateData({ pidRate: value });
  }, [updateData]);
  
  const handleIncludePIDChange = useCallback((value: boolean) => {
    updateData({ includePID: value });
  }, [updateData]);

  const handleRollFundingFeeChange = useCallback((value: boolean) => {
    updateData({ rollFundingFeeIntoLoan: value });
  }, [updateData]);

  return {
    handleHomePriceChange,
    handleDownPaymentChange,
    handleInterestRateChange,
    handleLoanTermChange,
    handleLoanTypeChange,
    handleZipCodeChange,
    handleIsVeteranChange,
    handleStateChange,
    handleCityChange,
    handlePropertyTaxChange,
    handlePIDRateChange,
    handleIncludePIDChange,
    handleInsuranceChange,
    handleHOAChange,
    handleCreditScoreChange,
    handlePropertyTypeChange,
    handleVADisabilityStatusChange,
    handleVAStatusChange,
    handleHomebuyingHistoryChange,
    handleSellerCreditChange,
    handleRollFundingFeeChange
  };
};
